import { Component, OnInit, Inject } from '@angular/core';
import { PageComponent, ICC_PAGE_DATA, ModalService } from '@icc/configurator/shared';
import { _, SharedFacade } from '@icc/configurator/shared';
import { Profile, ProfileType, Reinforcement } from '@icc/window-door/types';
import { CurrentConfiguratorService } from '@icc/configurator/shared';
import { IccProfileSideColors, Wood } from '@icc/construction-elements/types';
import { ConfigurationsService } from '@icc/configurator/shared';
import { Common, core, AppConfigFactory, APP_CONFIG, TranslateService, ConfiguratorsDataService } from '@icc/shared';
import { EventBusService } from '@icc/shared';
import { iccListItem, iccListTab, UnitConverterService } from '@icc/configurator/ui';
import { PriceBaseService } from '@icc/price/b2b';
import { PriceColorsService } from '../+price';
import { ProfileColorsService } from '../profile-colors.service';
import { BehaviorSubject, Subscription} from 'rxjs';
import { ProfileInfoComponent } from '../profile-info/profile-info.component';
import { ProfilesPriceService } from '../profiles-price.service';
import { ProfilesService } from '../profiles.service';
import { ProfilesUpdateService } from '../profiles-update.service';
import { IssuesService } from '@icc/shared';
import { IssueLevel } from '@icc/shared/helpers';

export interface iccProfileListItem extends iccListItem {
    profileCategoryId: string | number;
    price: {
        price_field: any;
        price_length: any;
        price_piece: any;
        price_construction: any;
        price_sash: any;
        price_area: any;
        price_percent: any;
    };
    type: ProfileType;
    originalType: ProfileType;
    selectedColor: Profile['selectedColor'];
    selectedWood: Profile['selectedWood'];
    width: Profile['width'];
    maxHeight: Profile['maxHeight'];
    minHeight: Profile['minHeight'];
    allowManualReinforcementChange?: boolean;
    widthOutType?: boolean;
    widthOutMin?: number;
    widthOutMax?: number;
    widthOutRanges?: Profile['widthOutRanges'];
    selectedWidth?: number;
    couplingType?: Profile['couplingType'];
    angle?: number
}

@Component({
    selector: 'icc-profiles-list-page',
    templateUrl: './profiles-list-page.component.html',
    styleUrls: ['./profiles-list-page.component.scss'],
    providers: [ProfilesUpdateService],
    standalone: false
})
export class ProfilesListPageComponent extends PageComponent implements OnInit {
    public title = _('WINDOW|Wybierz profil');
    public options = [];
    private subscriptions: Subscription[] = [];

    showTabs = false;
    showColorsWhenCoupling = this.CurConfService.conf === 'coupled_window';
    profiles: iccProfileListItem[] = [];
    selectedTypeTab = 'other';
    currency: any = null;
    selectedProfileId?: number;
    profileCategories: iccListTab[] = [];
    hiddenPrice = false;
    selectedColors$: BehaviorSubject<IccProfileSideColors> = new BehaviorSubject( {
        core: null,
        outer: null,
        inner: null,
        alushell: null,
    });

    maxAllowedWidth: number | undefined;
    clampedWidth: number | undefined;
    pendingCouplingClones: Map<string, { profileId: number; widthSettings: any }> = new Map();
    protected profilesUpdateService?: ProfilesUpdateService;

    ngOnChanges() {
        this.profiles.forEach(profile => {
            if (profile.widthOutType && profile.selectedWidth) {
                this.updateProfilePrice(profile);
            }
        });
    }


    tabs: iccListTab[] = [
        {
            id: 'fixed_mullion',
            name: this.translateService.instant('WINDOW|Słupki')
        },
        {
            id: 'false_mullion',
            name: this.translateService.instant('WINDOW|Słupki ruchome')
        },
        {
            id: 'sash',
            name: this.translateService.instant('WINDOW|Skrzydła')
        },
        {
            id: 'false_mullion_sash',
            name: this.translateService.instant('WINDOW|Skrzydła przymykowe')
        },
        {
            id: 'coupling',
            name: this.translateService.instant('WINDOW|Łączniki')
        },
        {
            id: 'extension',
            name: this.translateService.instant('WINDOW|Poszerzenie')
        },
        {
            id: 'other',
            name: this.translateService.instant('WINDOW|Inny profil')
        },
    ]

    dimensionUnit = this.unitConverterService.getUnit();

    get listPriceEnable() {
        return this.config().IccConfig.Offer.listPrice;
    }

    get listPriceEnableInMarket() {
        const conf = this.configurationsService.conf?.Current;
        const isFullDoorUnchecked = conf && 'Components' in conf && (conf as any).Components?.fullDoor === false;
        return isFullDoorUnchecked ? false : this.config().listPriceEnableInMarket;
    }

    constructor(
        protected sharedFacade: SharedFacade,
        protected profilesService: ProfilesService,
        protected profilesPriceService: ProfilesPriceService,
        protected priceBaseService: PriceBaseService,
        protected translateService: TranslateService,
        @Inject(APP_CONFIG) public config: AppConfigFactory,
        protected CurConfService: CurrentConfiguratorService,
        protected configurationsService: ConfigurationsService<'door'>,
        protected profileColorsService: ProfileColorsService,
        protected modalService: ModalService,
        protected issuesService: IssuesService,
        @Inject(ICC_PAGE_DATA)
        protected pageData: {
            profiles: Profile[];
            profilesPrices: any[];
            selectedProfile: Profile;
            system: any | null;
            type: ProfileType;
            currency: any;
            color?: IccProfileSideColors;
            wood?: Wood;
            reinforcement?: Reinforcement;
            profileCategories?: iccListTab[];
            hiddenPrice?: boolean;
            isDefaultLength?: boolean;
            length?: number;
            mullionId?: number | null;
        },
        protected unitConverterService: UnitConverterService,
        protected configuratorsDataService: ConfiguratorsDataService,
        protected priceColorsService: PriceColorsService,
        profilesUpdateService?: ProfilesUpdateService,
    ) {
        super();
        this.profilesUpdateService = profilesUpdateService;
    }

    ngOnInit() {
        const types: ProfileType[] = [];
        this.subscriptions.push(
            this.sharedFacade.currency$.subscribe(currency => {
                this.currency = currency;
            })
        );
        this.hiddenPrice =
            (this.pageData.hiddenPrice ?? false) ||
            (this.config().preset === 'b2c' && this.config().hidePricesInB2cConfigurator)
            || (this.config().IccConfig.Configurators.hideProfilePrice);
        this.pageData.profiles.forEach(profile => {
            if (this.pageData.color) {
                if (
                    this.pageData.selectedProfile
                    && this.pageData.selectedProfile.id === profile.id
                ) {
                    profile.selectedColor = this.pageData.color;
                    profile.selectedWood = this.pageData.wood;
                    this.selectedColors$.next(profile.selectedColor);
                } else if (profile.type === 'coupling' && profile.widthOutType) {
                    profile.selectedColor = core.copy(this.pageData.color);
                } else if (!profile.selectedColor || !profile.selectedColor.core) {
                    this.profileColorsService.setDefaultProfileColorSet(
                        this.configurationsService.conf.Current,
                        profile,
                        this.pageData.color,
                    );
                }
            }
            if (
                this.pageData.reinforcement
                && this.pageData.selectedProfile
                && this.pageData.selectedProfile.id === profile.id
            ) {
                profile.reinforcement = this.pageData.reinforcement;
            }

            if (!types.includes(profile.type)) {
                types.push(profile.type);
            }
        });

        const tabsAsCategories = this.pageData.profileCategories
            && types.length === 1;
        if (tabsAsCategories) {
            this.tabs = this.pageData.profileCategories?.map((category: any) => {
                return {id: category.id, name: category.name};
            }) || [];
        }

        this.profiles = this.pageData.profiles.map(profile => {
            const confCurrent = this.configurationsService.conf.Current;

            let widthBounds = this.getWidthBounds(profile);
            let defaultWidth = profile.selectedWidth ?? profile.widthOut ?? profile.widthOutMin ?? profile.widthOutMax ?? widthBounds.min;
            let selectedWidth = profile.widthOutType ? this.clampWidth(defaultWidth, widthBounds) : defaultWidth;
            if (profile.widthOutType && profile.type === 'coupling' && confCurrent) {
                const coupling = this.getCouplingByProfileId(confCurrent, Number(profile.id));
                if (coupling) {
                    if (typeof coupling.widthOutMin !== 'undefined') widthBounds.min = coupling.widthOutMin;
                    if (typeof coupling.widthOutMax !== 'undefined') widthBounds.max = coupling.widthOutMax;
                    if (typeof coupling.selectedWidth !== 'undefined') {
                        selectedWidth = this.clampWidth(coupling.selectedWidth, widthBounds);
                    } else if (typeof coupling.widthOut !== 'undefined') {
                        selectedWidth = this.clampWidth(coupling.widthOut, widthBounds);
                    } else {
                        selectedWidth = this.clampWidth(defaultWidth, widthBounds);
                    }
                }
            }

            if (profile.widthOutType && this.pageData.mullionId && confCurrent && profile.type !== 'coupling') {
                const sashesMullions = (confCurrent.Sashes || []).reduce((acc: any[], sash: any) => {
                    return acc.concat(sash?.intMullions || []);
                }, [] as any[]);
                const targetMullion = sashesMullions
                    .concat(confCurrent.Mullions || [])
                    .find((m: any) => Number(m.id) === Number(this.pageData.mullionId));
                if (targetMullion) {
                    const mullionProfile = (confCurrent as any).UsedProfiles?.find(
                        (p: any) => Number(p.id) === Number(targetMullion.profileId)
                    );
                    const effectiveProfileId = mullionProfile?._clonedFrom ?? targetMullion.profileId;

                    const currentOuter = targetMullion.direction === 'horizontal'
                        ? targetMullion.rHeight ?? 0
                        : targetMullion.rWidth ?? 0;
                    const maxAllowedWidth = this.calculateMaxAllowedWidth(Number(effectiveProfileId), currentOuter, confCurrent);
                    if (maxAllowedWidth !== undefined) {
                        widthBounds = {
                            ...widthBounds,
                            max: widthBounds.max !== undefined
                                ? Math.min(widthBounds.max, maxAllowedWidth)
                                : maxAllowedWidth
                        };
                        selectedWidth = this.clampWidth(selectedWidth ?? this.maxAllowedWidth, widthBounds);
                    }
                }
            }
            const originalType = this.getEffectiveOriginalType(profile, this.configurationsService.conf.Current, this.pageData.mullionId);
            const mappedProfile = {
                id: Number(profile.id),
                side: profile.side,
                profileCategoryId: profile.profileCategoryId,
                title: profile.name || profile.code,
                imageUrl: `/files/profile/${profile.img}`,
                description: `${profile.name}${profile.widthOutType && selectedWidth ? ` ${this.unitConverterService.milimetersToDimNoFormat(selectedWidth)}${this.dimensionUnit.unitSymbol}` : ''}`,
                price: profile.price,
                type: profile.type,
                originalType: originalType as ProfileType,
                selectedColor: profile.selectedColor,
                selectedWood: profile.selectedWood,
                width: profile.width,
                maxHeight: profile.maxHeight,
                minHeight: profile.minHeight,
                reinforcement: profile.reinforcement,
                widthOutType: profile.widthOutType,
                widthOutMin: profile.widthOutMin ?? widthBounds.min,
                widthOutMax: profile.widthOutMax ?? widthBounds.max,
                widthOutRanges: profile.widthOutRanges,
                selectedWidth,
                couplingType: profile.couplingType,
                allowManualReinforcementChange: profile.allowManualReinforcementChange ?? false,
                tabs: tabsAsCategories ? [profile.profileCategoryId] : [profile.type],
            };
            return mappedProfile;
        });
        this.profiles.forEach((p) => {
            if (p.widthOutType && p.selectedWidth) {
                this.updateProfilePrice(p);
            }
        });

        const selectedProfile: any = this.pageData.selectedProfile;
        const selectedProfileId = Number(selectedProfile?.id);
        if (Number.isFinite(selectedProfileId) && selectedProfileId < 0) {
            const clonedFrom = Number(selectedProfile?._clonedFrom ?? selectedProfile?.clonedFrom);
            this.selectedProfileId = Number.isFinite(clonedFrom)
                ? clonedFrom
                : Number(selectedProfile?.originalProfile ?? selectedProfileId);
        } else {
            this.selectedProfileId = this.pageData.selectedProfile && Number(this.pageData.selectedProfile.id);
        }
        if (types.length > 1 || tabsAsCategories) {
            this.showTabs = true;
        }
    }
    getEffectiveOriginalType(profile: Profile, conf: any, mullionId?: number | null): string {
        if (!profile) {
            return '';
        }
        if (profile.widthOutType && mullionId && conf) {
            const sashesMullions = (conf.Sashes || []).reduce((acc: any[], sash: any) => acc.concat(sash?.intMullions || []), [] as any[]);
            const targetMullion = sashesMullions
                .concat(conf.Mullions || [])
                .find((m: any) => Number(m.id) === Number(mullionId));
            if (targetMullion) {
                const mullionProfile = (conf as any).UsedProfiles?.find(
                    (p: any) => Number(p.id) === Number(targetMullion.profileId)
                );
                if (mullionProfile && this.isProfileInTwoParentIntSashes(mullionProfile.id, conf, targetMullion.id)) {
                    return 'structured_muntin_sash';
                }
            }
        }
        return profile.type;
    }


    hasZeroPrice(
        price: {
            price_field: any;
            price_length: any;
            price_piece: any;
            price_construction: any;
            price_sash: any;
            price_area: any;
            price_percent: any;
        },
        type: ProfileType | null = null
    ) {
        if (!price) {
            return false;
        }
        let requiredFields = [price.price_field, price.price_length, price.price_piece];
        if (
            (Common.isArray(this.pageData.type) && type === 'frame')
            || this.pageData.type === 'frame'
        ) {
            requiredFields.push(price.price_percent);
            requiredFields.push(price.price_construction);
        }
        if (
            (Common.isArray(this.pageData.type) && type === 'threshold')
            || this.pageData.type === 'threshold'
        ) {
            requiredFields = [price.price_construction, price.price_sash, price.price_length];
        } else if (
            !this.pageData.type
            || (Common.isArray(this.pageData.type)
                && type
                && ['coupling', 'extension', 'alignment', 'other'].includes(type))
            || ['coupling', 'extension', 'alignment', 'other'].includes(this.pageData.type)
        ) {
            requiredFields = [price.price_piece, price.price_length];
        } else if (
            (Common.isArray(this.pageData.type) && type === 'sandwich')
            || this.pageData.type === 'sandwich'
        ) {
            requiredFields = [price.price_area];
        }
        return requiredFields.every(p => p === 0);
    }

    hasNoPrice(
        price: {
            price_field: any;
            price_length: any;
            price_piece: any;
            price_construction: any;
            price_sash: any;
            price_area: any;
            price_percent: any;
        },
        type: ProfileType | null = null
    ) {
        if (!price) {
            return true;
        }
        let requiredFields = [price.price_field, price.price_length, price.price_piece];
        if (
            (Common.isArray(this.pageData.type) && type === 'frame')
            || this.pageData.type === 'frame'
        ) {
            requiredFields.push(price.price_percent);
            requiredFields.push(price.price_construction);
        }
        if (
            (Common.isArray(this.pageData.type) && type === 'threshold')
            || this.pageData.type === 'threshold'
        ) {
            requiredFields = [price.price_construction, price.price_sash, price.price_length];
        } else if (
            !this.pageData.type
            || (Common.isArray(this.pageData.type)
                && type
                && ['coupling', 'extension', 'alignment', 'other'].includes(type))
            || ['coupling', 'extension', 'alignment', 'other'].includes(this.pageData.type)
        ) {
            requiredFields = [price.price_piece, price.price_length];
        } else if (
            (Common.isArray(this.pageData.type) && type === 'sandwich')
            || this.pageData.type === 'sandwich'
        ) {
            requiredFields = [price.price_area];
        }
        const validFields = requiredFields.filter((p) => p !== null && !isNaN(Number(p)));
        return validFields.length === 0;

    }

    selectProfile(profileItem: iccProfileListItem) {
        if (profileItem.widthOutType && profileItem.selectedWidth !== undefined) {
            const conf = this.configurationsService.conf.Current;
            if (conf) {
                if (profileItem.type === 'coupling' || profileItem.originalType === 'coupling') {
                    const coupling = this.getCouplingByProfileId(conf, Number(profileItem.id));
                    if (coupling) {
                        const currentOuter = coupling.direction === 'horizontal' ? coupling.rHeight ?? 0 : coupling.rWidth ?? 0;
                        const maxAllowedWidth = this.calculateMaxAllowedWidth(Number(profileItem.id), currentOuter, conf);
                        if (maxAllowedWidth !== undefined && Number(profileItem.selectedWidth) > maxAllowedWidth) {
                            this.issuesService.simpleRegister(
                                'profile-width-too-large',
                                this.translateService.instant('WINDOW|Profil o zbyt dużej szerokości'),
                                `<b>${this.translateService.instant('WINDOW|Profil o szerokości {width} nie może zostać zastosowany.', { width: `${this.unitConverterService.milimetersToDimNoFormat(Number(profileItem.selectedWidth))}${this.dimensionUnit.unitSymbol}` })}</b><br>` +
                                `<ul style='margin: 0 0 0 1.5em;'><li>${this.translateService.instant('WINDOW|Maksymalna szerokość w tej konfiguracji wynosi: {maxWidth}', { maxWidth: `<b>${this.unitConverterService.milimetersToDimNoFormat(maxAllowedWidth)}${this.dimensionUnit.unitSymbol}</b>` })}</li>` +
                                `<li>${this.translateService.instant('WINDOW|Wprowadź mniejszą szerokość lub zmień konfigurację')}</li></ul>`,
                                this.configurationsService.conf.Current,
                                {
                                    logLevel: IssueLevel.WARN
                                }
                            );
                            return;
                        }
                    }
                } else if (this.pageData.mullionId) {
                    const sashesMullions = (conf.Sashes || []).reduce((acc: any[], sash: any) => acc.concat(sash?.intMullions || []), [] as any[]);
                    const targetMullion = sashesMullions.concat(conf.Mullions || []).find((m: any) => Number(m.id) === Number(this.pageData.mullionId));
                    if (targetMullion) {
                        const mullionProfile = (conf as any).UsedProfiles?.find((p: any) => Number(p.id) === Number(targetMullion.profileId));
                        const effectiveProfileId = mullionProfile?._clonedFrom ?? targetMullion.profileId;
                        const currentOuter = targetMullion.direction === 'horizontal' ? targetMullion.rHeight ?? 0 : targetMullion.rWidth ?? 0;
                        const maxAllowedWidth = this.calculateMaxAllowedWidth(Number(effectiveProfileId), currentOuter, conf);
                        if (maxAllowedWidth !== undefined && Number(profileItem.selectedWidth) > maxAllowedWidth) {
                            this.issuesService.simpleRegister(
                                'profile-width-too-large',
                                this.translateService.instant('WINDOW|Profil o zbyt dużej szerokości'),
                                `<b>${this.translateService.instant('WINDOW|Profil o szerokości {width} nie może zostać zastosowany.', { width: `${this.unitConverterService.milimetersToDimNoFormat(Number(profileItem.selectedWidth))}${this.dimensionUnit.unitSymbol}` })}</b><br>` +
                                `<ul style='margin: 0 0 0 1.5em;'><li>${this.translateService.instant('WINDOW|Maksymalna szerokość w tej konfiguracji wynosi: {maxWidth}', { maxWidth: `<b>${this.unitConverterService.milimetersToDimNoFormat(maxAllowedWidth)}${this.dimensionUnit.unitSymbol}</b>` })}</li>` +
                                `<li>${this.translateService.instant('WINDOW|Wprowadź mniejszą szerokość lub zmień konfigurację')}</li></ul>`,
                                this.configurationsService.conf.Current,
                                {
                                    logLevel: IssueLevel.WARN
                                }
                            );
                            return;
                        }
                    }
                }
            }
        }
        this.changeProfile(profileItem);
    }


    changeProfile(profileItem: iccProfileListItem) {
        let profile: Profile | undefined;
        const conf = this.configurationsService.conf.Current;
        let coupling: any;
        if (profileItem.widthOutType && (profileItem.type === 'coupling' || profileItem.originalType === 'coupling') && conf) {
            if(this.pageData.selectedProfile && this.pageData.selectedProfile.id < 0)
            {
                coupling = this.getCouplingByProfileId(conf, Number(this.pageData.selectedProfile.id));
            }
            else {
                coupling = this.getCouplingByProfileId(conf, Number(profileItem.id));
            }
            if (coupling) {
                const usedProfile = (conf as any).usedProfiles?.find((p: any) => Number(p.id) === Number(coupling.profileId));
                if (usedProfile && usedProfile.widthOutType) {
                    profile = core.copy(usedProfile);
                    (profile as any).originalType = 'coupling';
               }
            }
            else {
                if (profileItem.widthOutType) {
                    this.markProfileForCouplingCloning(Number(profileItem.id), profileItem.selectedWidth);
                }
            }
        }
        if (!profile && profileItem.widthOutType && this.pageData.mullionId && conf && profileItem.type !== 'coupling' && profileItem.originalType !== 'coupling') {
            const sashesMullions = (conf.Sashes || []).reduce((acc: any[], sash: any) => acc.concat(sash?.intMullions || []), [] as any[]);
            const targetMullion = sashesMullions.concat(conf.Mullions || []).find((m: any) => Number(m.id) === Number(this.pageData.mullionId));
            if (targetMullion) {
                const mullionProfile = (conf as any).UsedProfiles?.find((p: any) => Number(p.id) === Number(targetMullion.profileId));
                if (mullionProfile && mullionProfile.widthOutType) {
                    profile = core.copy(mullionProfile);
                    (profile as any).originalType = this.getEffectiveOriginalType(profile, conf, targetMullion.id);
                }
            }
        }
        if (!profile) {
            const original = this.pageData.profiles.find((p) => Number(p.id) === Number(profileItem.id));
            if (original) {
                profile = core.copy(original);
                (profile as any).originalType = this.getEffectiveOriginalType(profile, this.configurationsService.conf.Current, this.pageData.mullionId);
            }
        }
        if (profile) {
            const clonedFrom = (profile as any)._clonedFrom;
            let originalProfile;
            if(profile.id > -1)
            {
                originalProfile = profile.id; 
            }
            else if(profile. originalProfile){
                originalProfile = profile.originalProfile;
            }
            
            let parent: any = this.pageData.profiles.find((p) => Number(p.id) === Number(profileItem.id));
            if (clonedFrom !== undefined && clonedFrom !== null) {
                if (clonedFrom > -1) {
                    originalProfile = clonedFrom;
                } else {
                    const conf = this.configurationsService.conf.Current;
                    if (conf && Array.isArray(conf.UsedProfiles)) {
                        parent = conf.UsedProfiles.find((p: any) => Number(p.id) === Number(clonedFrom));
                    }
                    if (!parent && this.pageData && Array.isArray(this.pageData.profiles)) {
                        parent = this.pageData.profiles.find((p: any) => Number(p.id) === Number(clonedFrom));
                    }
                    if (parent) {
                        originalProfile = parent.originalProfile;
                    }
                }
            }
            (profile as any).originalProfile = originalProfile;
        }


        if (profile) {
            let previousOuter: number | undefined;
                if (profile.widthOutType && (profile.type === 'coupling' || this.pageData.mullionId) && profileItem.selectedWidth !== null && profileItem.selectedWidth !== undefined) {
                    previousOuter = profile.widthOut ?? profile.selectedWidth ?? profile.widthOutMin ?? profile.widthOutMax ?? profile.width;
                    if(this.pageData.selectedProfile && this.pageData.selectedProfile.id < 0) {
                        previousOuter = this.pageData.selectedProfile.widthOut ?? this.pageData.selectedProfile.selectedWidth ?? this.pageData.selectedProfile.widthOutMin ?? this.pageData.selectedProfile.widthOutMax ?? this.pageData.selectedProfile.width;
                    }
                    if ((!previousOuter || previousOuter <= 0) && profile.type !== 'coupling') {
                        const conf = this.configurationsService.conf.Current;
                        const sashesMullions = (conf.Sashes || []).reduce((acc: any[], sash: any) => acc.concat(sash?.intMullions || []), [] as any[]);
                        const targetMullion = sashesMullions
                            .concat(conf.Mullions || [])
                            .find((m: any) => Number(m.id) === Number(this.pageData.mullionId));
                        if (targetMullion) {
                            previousOuter = targetMullion.direction === 'horizontal' ? targetMullion.rHeight ?? 0 : targetMullion.rWidth ?? 0;
                        }
                    }
                }
                if (profile.widthOutType && profileItem.selectedWidth !== null && profileItem.selectedWidth !== undefined) {
                    profile.widthOut = profileItem.selectedWidth;
                    (profile as any).selectedWidth = profileItem.selectedWidth;
                    const conf = this.configurationsService.conf.Current;
                    if (conf && Array.isArray(conf.UsedProfiles)) {
                        const updated = conf.UsedProfiles.find(p => p.id === profile.id);
                    }
                    if (profileItem.angle !== null && profileItem.angle !== undefined) {
                        (profile as any).angle = profileItem.angle;
                    }
                } else if (profile.widthOutType) {
                    if (profileItem.selectedWidth !== null && profileItem.selectedWidth !== undefined) {
                        profile.widthOut = profileItem.selectedWidth;
                        (profile as any).selectedWidth = profileItem.selectedWidth;
                    }
                }
                if (profile.widthOutType && profileItem.selectedWidth !== null && profileItem.selectedWidth !== undefined && previousOuter !== undefined) {
                    let delta;
                    delta = profileItem.selectedWidth - previousOuter; 
                    if (delta !== 0) {
                        this.profilesUpdateService?.updateProfileValues(profile, delta, coupling, this.clampedWidth, this.pageData);
                    }
                }

            if (profile?.notStandardLength) {
                profile.isDefaultLength = this.pageData?.isDefaultLength === false ? false : true;
                profile.length = this.pageData?.length ?? 0;
            }
            this.sharedFacade.closePage(profile);
        }
    }
    updateProfilePrice(item: iccProfileListItem) {
        if (!item.widthOutType || !item.selectedWidth) {
            return;
        }
        const profileId = Number(item.id);
        const profile = this.pageData.profiles.find((p) => Number(p.id) === profileId);
        if (!profile) {
            return;
        }
        const previousOuter = Number.isFinite(Number(profile.widthOut)) ? Number(profile.widthOut) : Number(profile.width ?? 0);
        const bounds = this.getWidthBounds(profile);
        this.clampedWidth = this.clampWidth(item.selectedWidth, bounds);
        if (!this.clampedWidth) {
            return;
        }
        if (this.clampedWidth !== item.selectedWidth) {
            item.selectedWidth = this.clampedWidth;
        }
        
        if (profile.widthOutType) {
            profile.widthOut = this.clampedWidth;
            (profile as any).selectedWidth = this.clampedWidth;
            
            item.description = `${profile.name}${this.clampedWidth ? ` ${this.unitConverterService.milimetersToDimNoFormat(this.clampedWidth)}${this.dimensionUnit.unitSymbol}` : ''}`;
            const profileItem = this.profiles.find((p) => p.id === profileId);
            if (profileItem) {
                profileItem.selectedWidth = this.clampedWidth;
                profileItem.description = item.description;
            }
        }

        let rangeIndex: number | 'not-found' = 'not-found';
        let dynamicType: string = Common.isArray(this.pageData.type) ? item.originalType : this.pageData.type;
        if (profile.widthOutType && profile.widthOutRanges) {
            rangeIndex = profile.widthOutRanges.findIndex((range: any) => {
                const from = Number(range?.from);
                const to = Number(range?.to);
                return !isNaN(from) && !isNaN(to) && this.clampedWidth >= from && this.clampedWidth <= to;
            });
            if (rangeIndex !== -1) {
                dynamicType = `${item.originalType}_range_${rangeIndex}`;
            }
        }
        const colorFactor = this.priceColorsService.getSideProfileColorFactor({
            profile,
            profileColors: item.selectedColor,
            wood: item.selectedWood,
            system: this.configurationsService.conf.Current.System,
            colorGroups: this.configuratorsDataService.data.windowColorGroups || [],
        });
        item.price = this.profilesPriceService.getProfilePrice(
            profileId,
            dynamicType,
            this.pageData.system,
            item.selectedColor,
            this.pageData.profilesPrices,
            undefined,
            undefined,
            undefined,
            colorFactor,
            this.clampedWidth,
            { ...profile, originalProfile: (item as any).originalProfile, originalType: item.originalType }
        );
        
        if (item.widthOutType && item.price) {
            ['price_field', 'price_piece', 'price_construction'].forEach((field) => {
                if (item.price[field] == null) {
                    item.price[field] = 0;
                }
            });
        }
        profile.widthOut = previousOuter;
    }

    getWidthBounds(profile: Profile | iccProfileListItem) {
        let min: number | undefined;
        let max: number | undefined;

        if (profile.widthOutRanges && Array.isArray(profile.widthOutRanges)) {
            profile.widthOutRanges.forEach((range: any) => {
                const fromVal = Number(range?.from);
                const toVal = Number(range?.to);
                if (!isNaN(fromVal)) {
                    min = min === undefined ? fromVal : Math.min(min, fromVal);
                }
                if (!isNaN(toVal)) {
                    max = max === undefined ? toVal : Math.max(max, toVal);
                }
            });
        }

        const minCandidate = Number((profile as any).widthOutMin);
        const maxCandidate = Number((profile as any).widthOutMax);
        if (!isNaN(minCandidate)) {
            min = min === undefined ? minCandidate : Math.min(min, minCandidate);
        }
        if (!isNaN(maxCandidate)) {
            max = max === undefined ? maxCandidate : Math.max(max, maxCandidate);
        }

        return { min, max };
    }

    clampWidth(value: number | undefined, bounds: { min?: number; max?: number }) {
        if (value === null || value === undefined || isNaN(Number(value))) {
            return bounds.min;
        }
        let result = Number(value);
        if (bounds.min !== undefined && result < bounds.min) {
            result = bounds.min;
        }
        if (bounds.max !== undefined && result > bounds.max) {
            result = bounds.max;
        }
        return result;
    }

    calculateMaxAllowedWidth(
        profileId: number,
        currentOuter: number,
        conf: any
    ): number | undefined {
        if (!conf || !Array.isArray(conf.Sashes)) {
            return undefined;
        }
        const profile = this.profilesService.getProfile(profileId);
        const sashWithProfile = conf.Sashes.find((sash: any) => {
            if (!Array.isArray(sash.intMullions)) {
                return false;
            }
            if(profile && profile.type === 'coupling' ) {
                return conf.couplings.some((coupling: any) => {
                const mullionProfile = (conf as any).couplings?.find(
                    (p: any) => Number(p.id) === Number(coupling.profileId)
                );
                const effectiveProfileId = mullionProfile?._clonedFrom ?? coupling.profileId;
                return Number(effectiveProfileId) === profileId;
            });
            }
            else {
              return sash.intMullions.some((intMullion: any) => {
                const mullionProfile = (conf as any).UsedProfiles?.find(
                    (p: any) => Number(p.id) === Number(intMullion.profileId)
                );
                const effectiveProfileId = mullionProfile?._clonedFrom ?? intMullion.profileId;
                return Number(effectiveProfileId) === profileId;
            });  
            }
            
        });

        if (!sashWithProfile) {
            return undefined;
        }

        const targetMullion = sashWithProfile.intMullions.find((m: any) => {
            const mullionProfile = (conf as any).UsedProfiles?.find(
                (p: any) => Number(p.id) === Number(m.profileId)
            );
            const effectiveProfileId = mullionProfile?._clonedFrom ?? m.profileId;
            return Number(effectiveProfileId) === profileId && (!this.pageData.mullionId || Number(m.id) === Number(this.pageData.mullionId));
        });
        if (!targetMullion) {
            return undefined;
        }
        if (sashWithProfile.intSashes && Array.isArray(sashWithProfile.intSashes)) {
            const affectedIntSashes = sashWithProfile.intSashes.filter((intSash: any) => {
                if (targetMullion.direction === 'horizontal') {
                    return Number(intSash.nearMullions.top) === Number(targetMullion.id) 
                        || Number(intSash.nearMullions.bottom) === Number(targetMullion.id);
                } else {
                    return Number(intSash.nearMullions.left) === Number(targetMullion.id) 
                        || Number(intSash.nearMullions.right) === Number(targetMullion.id);
                }
            });

            const minWidthGlass = this.config().IccConfig.Configurators.minWidthGlass * 2 || 100;
            const relevantDimension = targetMullion.direction === 'horizontal' ? 'rHeight' : 'rWidth';
            
            let minAllowedWidth = Number.MAX_SAFE_INTEGER;
            affectedIntSashes.forEach((intSash: any) => {
                const intSashSize = intSash[relevantDimension] || 0;
                const maxDelta = Math.max(intSashSize - minWidthGlass, 0);
                const maxAllowed = currentOuter + maxDelta;
                minAllowedWidth = Math.min(minAllowedWidth, maxAllowed);
            });

            if (minAllowedWidth === Number.MAX_SAFE_INTEGER || affectedIntSashes.length === 0) {
                return undefined;
            }

            const result = Math.max(minAllowedWidth, Math.max((Number(currentOuter) || 0) - minWidthGlass, 0));
            return result;
        }

        return undefined;
    }

    isProfileInTwoParentIntSashes(profileId: number, conf: any, mullionId?: number | null): { boolean } | any {
        if (!conf || !Array.isArray(conf.Sashes)) {
            return false;
        }
        for (const sash of conf.Sashes) {
            if (!Array.isArray(sash.intMullions)) continue;
            for (const intMullion of sash.intMullions) {
                const mullionProfile = (conf as any).UsedProfiles?.find(
                    (p: any) => Number(p.id) === Number(intMullion.profileId)
                );
                let matchId = profileId;
                if (profileId < 0 && mullionProfile && typeof mullionProfile.originalProfile) {
                    matchId = mullionProfile.originalProfile;
                }
                const effectiveProfileId = mullionProfile?.originalProfile ?? intMullion.profileId;
                const matchesProfile = Number(effectiveProfileId) === matchId;
                const matchesMullion = !mullionId || Number(intMullion.id) === Number(mullionId);
                if (matchesProfile && matchesMullion) {
                    if (Array.isArray(sash.intSashes)) {
                        const sashesWithThisMullion = sash.intSashes.filter((intSash: any) => {
                            const near = intSash.nearMullions || {};
                            return Object.values(near).some((val: any) => Number(val) === Number(intMullion.id));
                        });
                        if (sashesWithThisMullion.length === 2 &&
                            sashesWithThisMullion.every((intSash: any) => intSash.parentId !== undefined && intSash.parentId !== null)) {
                            return true;
                        }
                    }
                    return false;
                }
            }
        }
        return false;
    }
    getCouplingByProfileId(conf: any, profileId: number): any | undefined {
        if (!conf || !Array.isArray(conf.couplings)) return undefined;
        return conf.couplings.find((c: any) => {
            if (Number(c.profileId) === profileId) return true;
            if (typeof c._clonedFrom !== 'undefined' && Number(c._clonedFrom) === profileId) return true;
            if (profileId < 0 && typeof c.originalProfile !== 'undefined' && Number(c.originalProfile) === profileId) return true;
            return false;
        });
    }
    markProfileForCouplingCloning(profileId: number, selectedWidth?: number) {
        const key = 'next'; 
        
        const cloneData = {
            profileId,
            selectedWidth,
            widthSettings: {
                selectedWidth,
                widthOut: selectedWidth
            }
        };
        this.pendingCouplingClones.set(key, cloneData);
        this.profilesService.addPendingCouplingClone(key, cloneData);
    }

    getPendingCouplingClone(key: string = 'next'): { profileId: number; widthSettings: any } | undefined {
        return this.pendingCouplingClones.get(key);
    }

    clearPendingCouplingClone(key: string = 'next') {
        this.pendingCouplingClones.delete(key);
    }

    public hasPendingCouplingClones(): boolean {
        return this.pendingCouplingClones.size > 0;
    }

    public getAllPendingCouplingClones(): Map<string, { profileId: number; widthSettings: any }> {
        return new Map(this.pendingCouplingClones);
    }




    openModalColors(profile: Profile) {
        this.profileColorsService
            .openProfileColorsModal(profile, this.pageData.selectedProfile, this.pageData.profilesPrices, this.selectedColors$)
            .then(selection => {
                const colorFactor = this.priceColorsService.getSideProfileColorFactor({
                    profile,
                    profileColors: selection ? selection.colors : profile.selectedColor,
                    wood: profile.selectedWood,
                    system: this.configurationsService.conf.Current.System,
                    colorGroups: this.configuratorsDataService.data.windowColorGroups || [],
                });

                profile.price = this.profilesPriceService.getProfilePrice(
                    profile.id,
                    Common.isArray(this.pageData.type) ? profile.type : this.pageData.type,
                    this.pageData.system,
                    selection ? selection.colors : profile.selectedColor,
                    this.pageData.profilesPrices,
                    undefined,
                    undefined,
                    undefined,
                    colorFactor,
                );

                profile.selectedColor = core.copy(
                    selection ? selection.colors : profile.selectedColor
                );
                profile.selectedWood = core.copy(selection ? selection.wood : profile.selectedWood);
            });
    }

    priceMarket(price: number) {
        return this.priceBaseService.addMarginMarketFactor(price);
    }

    clickShowInfoButton(item: iccListItem) {
        const profile = this.profilesService.getProfile(Number(item?.id));
        profile && this.modalService
            .open({
                pageComponent: ProfileInfoComponent,
                resolve: {
                    profile: () => profile,
                    button: () => true
                },
            });
    }

    isWysiwygEditorContentNotEmpty(item: iccListItem) {
        const profile = this.profilesService.getProfile(Number(item?.id));

        return profile && Boolean(profile?.wysiwygEditorContent?.length);
    }
}

